const HISTORY_POSITION = "sbEditorHistoryPosition";

export default function registerBrowserNavigationGuard({
    $rootScope,
    $location,
    $urlRouter,
    $window,
    $exceptionHandler,
    getLeaveGuard,
}) {
    let position = $location.state()?.[HISTORY_POSITION] ?? 0;
    let currentUrl = $location.url();
    let pendingNavigation;
    let replayPosition;
    let navigationApproved = false;
    let externalUrlChange = false;
    let destroyed = false;

    function stampPosition() {
        $location
            .state({
                ...$location.state(),
                [HISTORY_POSITION]: position,
            })
            .replace();
    }

    stampPosition();

    const removeStartListener = $rootScope.$on(
        "$locationChangeStart",
        (event, newUrl, oldUrl) => {
            externalUrlChange =
                newUrl !== oldUrl && newUrl === $window.location.href;
        }
    );

    const removeSuccessListener = $rootScope.$on(
        "$locationChangeSuccess",
        () => {
            const destinationPosition = $location.state()?.[HISTORY_POSITION];
            const destinationUrl = $location.url();

            if (pendingNavigation) {
                if (destinationPosition === position) {
                    pendingNavigation.onRestored?.();
                    pendingNavigation.onRestored = undefined;
                } else if (Number.isInteger(destinationPosition)) {
                    $window.history.go(position - destinationPosition);
                } else {
                    $exceptionHandler(
                        new Error(
                            "Cannot restore an untracked editor history entry"
                        )
                    );
                }
                return;
            }

            if (
                replayPosition === destinationPosition &&
                replayPosition !== undefined
            ) {
                replayPosition = undefined;
                navigationApproved = true;
                position = destinationPosition;
                currentUrl = destinationUrl;
                $urlRouter.sync();
                return;
            }

            if (!Number.isInteger(destinationPosition)) {
                if (externalUrlChange && getLeaveGuard()) {
                    $exceptionHandler(
                        new Error(
                            "Cannot restore an untracked editor history entry"
                        )
                    );
                }
                if (destinationUrl !== currentUrl) {
                    position++;
                }
                currentUrl = destinationUrl;
                stampPosition();
                $urlRouter.sync();
                return;
            }

            const leaveGuard = getLeaveGuard();
            if (
                !externalUrlChange ||
                !leaveGuard ||
                destinationUrl === currentUrl
            ) {
                position = destinationPosition;
                currentUrl = destinationUrl;
                $urlRouter.sync();
                return;
            }

            const restored = new Promise((resolve) => {
                pendingNavigation = { onRestored: resolve };
            });
            $window.history.go(position - destinationPosition);
            restored
                .then(() => leaveGuard())
                .then((approved) => {
                    pendingNavigation = undefined;
                    if (approved && !destroyed) {
                        replayPosition = destinationPosition;
                        $window.history.go(destinationPosition - position);
                    }
                })
                .catch((error) => {
                    pendingNavigation = undefined;
                    $exceptionHandler(error);
                });
        }
    );

    const clearApproval = () => {
        navigationApproved = false;
    };
    const removeStateSuccessListener = $rootScope.$on(
        "$stateChangeSuccess",
        clearApproval
    );
    const removeStateErrorListener = $rootScope.$on(
        "$stateChangeError",
        clearApproval
    );
    $rootScope.$on("$destroy", () => {
        destroyed = true;
        removeStartListener();
        removeSuccessListener();
        removeStateSuccessListener();
        removeStateErrorListener();
    });

    return {
        consumeNavigationApproval() {
            const approved = navigationApproved;
            navigationApproved = false;
            return approved;
        },
    };
}
